import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── 1시간 미사용 자동 로그아웃 ────────────────────────────────────────
// - 마우스/키보드/스크롤 등 실제 조작이 있을 때마다 AuthContext.touchActivity()로 "마지막 활동
//   시각"을 sessionStorage에 기록(+다른 탭에도 방송)하고, 10초마다 지금 시각과 비교해서 유휴
//   시간을 확인한다. 1시간에서 5분 남은 시점부터는 경고 모달을 띄우고, 그래도 계속 반응이
//   없으면 1시간이 되는 순간 자동 로그아웃한다.
// - 생산전광판(/display)은 아무도 마우스를 만지지 않는 게 정상적인 화면이라 예외로 둔다 —
//   그 화면을 보고 있는 동안은 계속 "지금 막 활동함"으로 취급해서(다른 탭에도 방송되어 전체
//   세션이 함께 예외 처리됨) 유휴시간이 쌓이지 않게 하고, 그 화면을 벗어난 뒤에는 그 시점부터
//   다시 1시간을 새로 센다.
// - 여러 탭을 동시에 열어둔 경우, 어느 한 탭에서라도 활동이 있으면(또는 어느 한 탭이 생산전광판을
//   보여주고 있으면) AuthContext의 방송을 통해 모든 탭이 함께 "지금 활동 중"으로 취급된다.
// - 컴퓨터가 절전모드로 들어갔다 깨어나는 경우에도, setInterval의 "틱 횟수"가 아니라 실제
//   시각(Date.now()) 차이로 계산하기 때문에 정확하게 동작한다.
const IDLE_LIMIT_MS = 1 * 60 * 60 * 1000;   // 1시간
const WARNING_BEFORE_MS = 5 * 60 * 1000;    // 만료 5분 전부터 경고 표시
const CHECK_INTERVAL_MS = 10 * 1000;        // 10초마다 점검(경고 카운트다운을 부드럽게 보여주기 위함)
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
const DISPLAY_PATH = '/display';

function formatRemaining(ms) {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}분 ${String(s).padStart(2, '0')}초`;
}

export default function AutoLogout() {
  const { user, logout, touchActivity } = useAuth();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  const [warningRemainingMs, setWarningRemainingMs] = useState(null); // null이면 경고 숨김

  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!user) { setWarningRemainingMs(null); return; }

    const touch = () => touchActivity();
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, touch, { passive: true }));

    const timer = setInterval(() => {
      if (pathRef.current.startsWith(DISPLAY_PATH)) {
        touchActivity(true); // 생산전광판 예외 — 계속 "방금 활동함"으로 취급 (다른 탭에도 전파)
        setWarningRemainingMs(null);
        return;
      }
      const last = Number(sessionStorage.getItem('lastActivityAt') || Date.now());
      const elapsed = Date.now() - last;

      if (elapsed >= IDLE_LIMIT_MS) {
        setWarningRemainingMs(null);
        logout('inactivity');
        return;
      }
      if (elapsed >= IDLE_LIMIT_MS - WARNING_BEFORE_MS) {
        setWarningRemainingMs(IDLE_LIMIT_MS - elapsed);
      } else {
        setWarningRemainingMs(null);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, touch));
      clearInterval(timer);
    };
  }, [user, logout, touchActivity]);

  const stayLoggedIn = () => {
    touchActivity(true);
    setWarningRemainingMs(null);
  };

  if (warningRemainingMs == null) return null;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, width:380, maxWidth:'90vw', padding:'24px 24px 20px' }}>
        <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>⏰ 자동 로그아웃 안내</h3>
        <p style={{ margin:'12px 0 0', fontSize:13.5, color:'#555', lineHeight:1.6 }}>
          장시간 사용 기록이 없어 <strong>{formatRemaining(warningRemainingMs)}</strong> 후 자동으로
          로그아웃됩니다. 계속 사용하시려면 아래 버튼을 눌러주세요.
        </p>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:20 }}>
          <button onClick={() => logout('manual')}
            style={{ background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            지금 로그아웃
          </button>
          <button onClick={stayLoggedIn}
            style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
              padding:'9px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            계속 사용
          </button>
        </div>
      </div>
    </div>
  );
}